import React from 'react';
import { Button, Descriptions, Divider, Modal, Table, Tag } from 'antd';

/**
 * 拣货阶段快照的只读回看（库存非标准操作终审页用）。
 *
 * 非标准指令的阶段 1 拣货是标准的 PICK 步骤，和常规指令是同一套组件、同一份快照数据。这里的展示逻辑和样式
 * 照搬常规指令终审页的"阶段快照 → 查看快照 → 可读视图 → 拣货"（FinalReview.tsx），只做了拷贝，没有改动常规页：
 * - 目标是历史指令产出（IOC）：仓位表与拣货结果 + 拣货操作记录；
 * - 目标是普通库存单元：汇总指标、按仓储容器汇总、按目标汇总、拣货操作记录。
 * 以后常规页的这块样式改了，这里需要同步改。
 */

type AnyObj = Record<string, any>;

interface Props {
  /** 通用阶段快照里的拣货快照内容（stats、pick_logs、ui_context、target_qty_map 等） */
  snapshot: AnyObj;
  /** 指令的操作对象明细（instruction.targets_detail）：快照里缺目标数量时用它兜底 */
  targets?: AnyObj[];
}

function fmtDateTime(v: any): string {
  if (v == null) return '-';
  try {
    if (typeof v === 'number' && Number.isFinite(v)) {
      const n0 = Math.floor(v);
      const ms = n0 > 1_000_000_000_000 ? n0 : (n0 > 1_000_000_000 ? n0 * 1000 : 0);
      if (ms > 0) {
        const d = new Date(ms);
        if (!Number.isNaN(d.getTime())) return formatDate(d);
      }
    }
  } catch { /* 继续按字符串处理 */ }
  const raw = String(v || '').trim();
  if (!raw) return '-';
  try {
    if (/^\d{10,16}$/.test(raw)) {
      const n0 = Math.floor(Number(raw));
      const ms = n0 > 1_000_000_000_000 ? n0 : (n0 > 1_000_000_000 ? n0 * 1000 : 0);
      if (ms > 0) {
        const d = new Date(ms);
        if (!Number.isNaN(d.getTime())) return formatDate(d);
      }
    }
  } catch { /* 继续 */ }
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    return formatDate(d);
  } catch {
    return raw;
  }
}

function formatDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

const mono = (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span>;
const pager = (defaultPageSize: number, sizes: string[]) => ({
  defaultPageSize,
  showSizeChanger: true,
  pageSizeOptions: sizes,
  showQuickJumper: true,
  showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
});

const parseContentLines = (raw: any): string[] => {
  const txt = String(raw || '').trim();
  if (!txt || txt === '-') return [];
  const byLine = txt.split(/\r?\n/).map(s => String(s || '').trim()).filter(Boolean);
  if (byLine.length > 1) return byLine;
  const bySemi = txt.split(/[；;]+/).map(s => String(s || '').trim()).filter(Boolean);
  if (bySemi.length > 1) return bySemi;
  const byPipe = txt.split(/\s*\|\s*/).map(s => String(s || '').trim()).filter(Boolean);
  if (byPipe.length > 1) return byPipe;
  return [txt];
};

const sumBy = (arr: AnyObj[], keyFn: (x: AnyObj) => string) => {
  const m = new Map<string, { key: string; qty: number; rows: AnyObj[]; off_shelf_count: number }>();
  (arr || []).forEach(r => {
    const k = String(keyFn(r) || '').trim() || '-';
    const q = Math.floor(Number(r?.qty || 0) || 0);
    if (!m.has(k)) m.set(k, { key: k, qty: 0, rows: [], off_shelf_count: 0 });
    const it = m.get(k)!;
    it.qty += (Number.isFinite(q) ? q : 0);
    if (r?.off_shelf) it.off_shelf_count += 1;
    it.rows.push(r);
  });
  return Array.from(m.values()).sort((a, b) => (b.qty - a.qty) || a.key.localeCompare(b.key, undefined, { numeric: true, sensitivity: 'base' }));
};

const targetCodeOf = (r: AnyObj) => {
  const snap0 = (r?.target?.snapshot || r?.snapshot || {}) as AnyObj;
  return String(snap0?.system_barcode || r?.inventory_unit_system_barcode || r?.target?.inventory_unit_system_barcode || '').trim();
};

const NonStdPickSnapshotView: React.FC<Props> = ({ snapshot, targets }) => {
  const snap = (snapshot || {}) as AnyObj;
  const stats = (snap?.stats || {}) as AnyObj;
  const logs: AnyObj[] = Array.isArray(snap?.pick_logs) ? snap.pick_logs : [];
  const uiCtx = (snap?.ui_context && typeof snap.ui_context === 'object') ? (snap.ui_context as AnyObj) : {};
  const pickMode = String(snap?.pick_mode || uiCtx?.pick_mode || '').trim().toLowerCase();
  const isIocPick = pickMode === 'ioc' || !!uiCtx?.ioc_snapshot_preview;

  // ---------------- 目标是历史指令产出（IOC） ----------------
  if (isIocPick) {
    const previewInSnap = (uiCtx?.ioc_snapshot_preview && typeof uiCtx.ioc_snapshot_preview === 'object')
      ? (uiCtx.ioc_snapshot_preview as AnyObj)
      : ({} as AnyObj);
    const pickedSet = new Set<string>(logs.map(r => String(r?.container_code || '').trim()).filter(Boolean));
    const iocRowsRaw = Array.isArray(previewInSnap?.rows) ? previewInSnap.rows : [];
    const iocRows = (() => {
      if (iocRowsRaw.length > 0) {
        return iocRowsRaw.map((r: any, idx: number) => {
          const code = String(r?.code || r?.output_code || r?.display_code || r?.container_code || `IOC-${idx + 1}`).trim();
          return {
            __k: `${code}-${idx}`,
            code,
            location: String(r?.location || r?.location_code || '-'),
            instructionCode: String(r?.instructionCode || r?.instruction_code || '-'),
            containerType: String(r?.containerType || r?.container_type_display || '-'),
            contentType: String(r?.contentType || r?.output_content_kind_display || '-'),
            contentSummary: String(r?.contentSummary || r?.output_contents_summary || '-'),
            contentDetail: String(r?.contentDetail || r?.output_contents_detail || '-'),
            picked: pickedSet.has(code) || !!r?.picked,
          };
        });
      }
      const uniq = Array.from(new Set(logs.map(r => String(r?.container_code || '').trim()).filter(Boolean)));
      return uniq.map((code: string, idx: number) => ({
        __k: `${code}-${idx}`, code, location: '-', instructionCode: '-', containerType: '-', contentType: '-',
        contentSummary: '-', contentDetail: '-', picked: pickedSet.has(code),
      }));
    })();
    const iocStats = (() => {
      const total = Number.isFinite(Number(previewInSnap?.total)) ? Math.floor(Number(previewInSnap.total)) : iocRows.length;
      const picked = Number.isFinite(Number(previewInSnap?.picked))
        ? Math.floor(Number(previewInSnap.picked))
        : iocRows.filter((r: any) => !!r?.picked).length;
      const todo = Number.isFinite(Number(previewInSnap?.todo)) ? Math.floor(Number(previewInSnap.todo)) : Math.max(total - picked, 0);
      return { total, picked, todo };
    })();
    return (
      <div style={{ lineHeight: 1.7, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ background: '#f0f9ff', border: '1px solid #91caff', borderRadius: 8, padding: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontWeight: 700 }}>仓位表与拣货结果</div>
            <div style={{ color: '#595959' }}>
              目标IOC {iocStats.total} ｜ 已拣出 {iocStats.picked} ｜ 待拣出 {iocStats.todo} ｜ 记录 {logs.length}
            </div>
          </div>
          <Table
            size="small"
            tableLayout="fixed"
            rowKey={(r: any) => String(r?.__k || r?.code || '')}
            dataSource={iocRows}
            onRow={(record: any) => ({ style: record?.picked ? { background: '#f6ffed' } : undefined })}
            pagination={pager(20, ['10', '20', '50', '100'])}
            columns={[
              { title: 'IOC系统编码', dataIndex: 'code', key: 'code', width: 220, ellipsis: true, render: mono },
              { title: '仓位', dataIndex: 'location', key: 'location', width: 120, ellipsis: true },
              { title: '来源历史指令', dataIndex: 'instructionCode', key: 'instructionCode', width: 180, ellipsis: true, render: mono },
              { title: '容器类型', dataIndex: 'containerType', key: 'containerType', width: 120, ellipsis: true },
              { title: '内容类型', dataIndex: 'contentType', key: 'contentType', width: 120, ellipsis: true },
              { title: '容器内容统计', dataIndex: 'contentSummary', key: 'contentSummary', width: 130, ellipsis: true },
              {
                title: '容器内容',
                dataIndex: 'contentDetail',
                key: 'contentDetail',
                width: 320,
                render: (v: any, r: any) => {
                  const lines = parseContentLines(v);
                  if (lines.length === 0) return '-';
                  const preview = lines.slice(0, 3);
                  return (
                    <div>
                      {preview.map((line: string, idx: number) => (
                        <div key={`${String(r?.code || '')}-line-${idx}`} style={{ marginBottom: idx === preview.length - 1 ? 0 : 2, wordBreak: 'break-all' }}>{line}</div>
                      ))}
                      {lines.length > 3 ? (
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0, height: 'auto', lineHeight: 1.2, marginTop: 2 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            Modal.info({
                              title: `容器内容完整记录（${String(r?.code || '-')})`,
                              width: 760,
                              centered: true,
                              content: (
                                <div style={{ maxHeight: 420, overflow: 'auto', paddingRight: 8 }}>
                                  {lines.map((line: string, i: number) => (
                                    <div key={`${String(r?.code || '-')}-${i}`} style={{ marginBottom: 6, wordBreak: 'break-all' }}>{i + 1}. {line}</div>
                                  ))}
                                </div>
                              ),
                            });
                          }}
                        >
                          [...]
                        </Button>
                      ) : null}
                    </div>
                  );
                },
              },
              {
                title: '拣出状态',
                dataIndex: 'picked',
                key: 'picked',
                width: 110,
                align: 'center' as const,
                render: (v: any) => <Tag color={v ? 'green' : 'default'}>{v ? '已拣出' : '待拣出'}</Tag>,
              },
            ]}
            scroll={{ x: 1500, y: 360 }}
          />
        </div>

        <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8, padding: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontWeight: 700 }}>拣货操作记录</div>
            <div style={{ color: '#595959' }}>记录总数 {logs.length}</div>
          </div>
          <Table
            size="small"
            tableLayout="fixed"
            rowKey={(_, idx) => String(idx)}
            dataSource={logs}
            pagination={pager(10, ['5', '10', '20', '50'])}
            columns={[
              { title: '操作时间', dataIndex: 'operated_at', key: 'operated_at', width: 180, ellipsis: true, render: (v: any) => mono(fmtDateTime(v)) },
              { title: 'IOC系统编码', dataIndex: 'container_code', key: 'container_code', width: 220, ellipsis: true, render: mono },
              { title: '动作', key: 'action', width: 100, align: 'center' as const, render: () => '扫码拣出' },
            ]}
            scroll={{ y: 260 }}
          />
        </div>
      </div>
    );
  }

  // ---------------- 目标是普通库存单元 ----------------
  const instrTargets: AnyObj[] = Array.isArray(targets) ? targets : [];
  // 指令目标明细：补齐目标数量（尤其是 use_all_available 导致 target_qty_map 为 null 的情况）
  const expectedQtyMap = (() => {
    const m = new Map<string, number>();
    instrTargets.forEach(r => {
      const code = targetCodeOf(r);
      if (!code) return;
      const n = Math.floor(Number(r?.expected_qty ?? 0));
      if (Number.isFinite(n) && n >= 0) m.set(code, n);
    });
    return m;
  })();
  const useAllMapFromInstr = (() => {
    const m = new Map<string, boolean>();
    instrTargets.forEach(r => {
      const code = targetCodeOf(r);
      if (code) m.set(code, !!r?.use_all_available);
    });
    return m;
  })();
  // 目标数量：优先使用后端固化的 target_qty_map；没有时再尝试从 ui_context 推断
  const targetQtyMap = (() => {
    const m = new Map<string, number | null>();
    const raw = snap?.target_qty_map;
    if (raw && typeof raw === 'object') {
      Object.keys(raw).forEach(k => {
        const code = String(k || '').trim();
        if (!code) return;
        const v = (raw as any)[k];
        if (v == null) { m.set(code, null); return; }
        const n = Math.floor(Number(v));
        if (Number.isFinite(n) && n >= 0) m.set(code, n);
      });
      return m;
    }
    const roots: any[] = [uiCtx?.targetSummary, uiCtx?.target_summary, uiCtx?.snapshot?.targetSummary, uiCtx?.snapshot?.target_summary].filter(Boolean);
    for (const root of roots) {
      const rows = Array.isArray(root?.rows) ? root.rows : (Array.isArray(root) ? root : []);
      for (const r of rows) {
        const code = String(r?.target_code || r?.unit_code || r?.code || '').trim();
        if (!code) continue;
        const q = Number(r?.target_qty ?? r?.targetQty ?? r?.expected_qty ?? r?.expectedQty ?? r?.expected ?? r?.qty_expected ?? r?.qty_target);
        if (Number.isFinite(q) && q >= 0) m.set(code, Math.floor(q));
      }
    }
    return m;
  })();
  // 分拣标识：优先使用后端固化的 target_mark_map
  const targetMarkMap = (() => {
    const m = new Map<string, string>();
    const raw = snap?.target_mark_map;
    if (raw && typeof raw === 'object') {
      Object.keys(raw).forEach(k => {
        const code = String(k || '').trim();
        const v = String((raw as any)[k] || '').trim();
        if (code && v) m.set(code, v);
      });
    }
    return m;
  })();
  // 是否 use_all_available：优先使用后端固化的 target_use_all_map；没有则用指令 targets_detail 兜底
  const targetUseAllMap = (() => {
    const m = new Map<string, boolean>();
    const raw = snap?.target_use_all_map;
    if (raw && typeof raw === 'object') {
      Object.keys(raw).forEach(k => {
        const code = String(k || '').trim();
        if (code) m.set(code, !!(raw as any)[k]);
      });
    }
    return m;
  })();

  const byContainer = sumBy(logs, r => String(r?.container_code || '').trim());
  const byTarget = sumBy(logs, r => String(r?.target_code || '').trim());
  const pickTotalQtyNum = (() => {
    const n = Number(stats?.total_qty ?? stats?.totalQty ?? 0);
    return Number.isFinite(n) ? Math.floor(n) : 0;
  })();
  // 拣货目标总数量：以"按目标汇总"表格的目标集合为准求和（target_qty_map 优先，缺失时用指令 expected_qty 兜底）
  const targetTotalQtyNum = (() => {
    let s = 0;
    byTarget.forEach(row => {
      const code = String(row?.key || '').trim();
      if (!code) return;
      const q0 = targetQtyMap.get(code);
      const q = (q0 == null ? expectedQtyMap.get(code) : q0);
      if (Number.isFinite(q as any)) s += Math.floor(Number(q));
    });
    return s;
  })();
  const pickDiffNum = pickTotalQtyNum - targetTotalQtyNum;
  const diffUI = (() => {
    const base = { borderRadius: 4, padding: '1px 8px', fontWeight: 800, display: 'inline-block' } as React.CSSProperties;
    if (!Number.isFinite(pickDiffNum)) return { label: '拣货总差额', contentStyle: undefined as any, tag: null as any };
    const pick = (bg: string, bd: string, fg: string, text: string) => ({
      label: '拣货总差额',
      contentStyle: { backgroundColor: bg, border: `1px solid ${bd}`, color: fg, fontWeight: 800 } as React.CSSProperties,
      tag: <span style={{ ...base, backgroundColor: bg, border: `1px solid ${bd}`, color: fg }}>{text}</span>,
    });
    if (pickDiffNum < 0) return pick('#e6f4ff', '#91caff', '#0958d9', '拣货不足');
    if (pickDiffNum === 0) return pick('#f6ffed', '#b7eb8f', '#389e0d', '正好完成拣货目标');
    return pick('#fff2e8', '#ffbb96', '#d4380d', '超额拣货');
  })();

  return (
    <div style={{ lineHeight: 1.75 }}>
      <Descriptions size="small" column={2} bordered>
        <Descriptions.Item label="阶段">拣货</Descriptions.Item>
        <Descriptions.Item label="提交时间">{fmtDateTime(snap?.submitted_at)}</Descriptions.Item>
        <Descriptions.Item label="拣货涉及仓储容器">{String(stats?.containers ?? '-')}</Descriptions.Item>
        <Descriptions.Item label="拣货记录数">{String(logs.length)}</Descriptions.Item>
        <Descriptions.Item label="拣货目标总数量">{String(targetTotalQtyNum)}</Descriptions.Item>
        <Descriptions.Item label="拣货目标种类">{String(stats?.kinds ?? '-')}</Descriptions.Item>
        <Descriptions.Item label="拣货总数量">{String(pickTotalQtyNum)}</Descriptions.Item>
        <Descriptions.Item
          label={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><span>{diffUI.label}</span>{diffUI.tag}</span>}
          contentStyle={diffUI.contentStyle}
        >
          {String(pickDiffNum)}
        </Descriptions.Item>
      </Descriptions>
      {Object.keys(uiCtx || {}).length ? (
        <>
          <Divider style={{ margin: '10px 0' }} />
          <div style={{ fontWeight: 800, marginBottom: 6 }}>UI上下文</div>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 220, overflowY: 'auto', overflowX: 'hidden', background: '#f6f6f6', padding: 10, borderRadius: 6 }}>
            {JSON.stringify(uiCtx, null, 2)}
          </pre>
        </>
      ) : null}
      <Divider style={{ margin: '10px 0' }} />
      <div style={{ fontWeight: 800, marginBottom: 6 }}>按仓储容器汇总</div>
      <Table
        size="small"
        tableLayout="fixed"
        rowKey={(r: any) => String(r?.key || '')}
        pagination={pager(10, ['10', '20', '50', '100'])}
        dataSource={byContainer}
        columns={[
          { title: '容器', dataIndex: 'key', key: 'key', width: 220, ellipsis: true, render: mono },
          { title: '数量合计', dataIndex: 'qty', key: 'qty', width: 110 },
          {
            title: '已下架',
            dataIndex: 'off_shelf_count',
            key: 'off_shelf_count',
            width: 110,
            render: (v: any) => {
              const n = Math.floor(Number(v || 0) || 0);
              if (!Number.isFinite(n) || n <= 0) return <span />;
              return <span style={{ color: '#cf1322', fontWeight: 800 }}>已下架</span>;
            },
          },
        ]}
        scroll={{ y: 240 }}
      />

      <Divider style={{ margin: '10px 0' }} />
      <div style={{ fontWeight: 800, marginBottom: 6 }}>按目标汇总</div>
      <Table
        size="small"
        tableLayout="fixed"
        rowKey={(r: any) => String(r?.key || '')}
        pagination={pager(10, ['10', '20', '50', '100'])}
        dataSource={byTarget}
        columns={[
          { title: '目标(库存单元)', dataIndex: 'key', key: 'key', width: 220, ellipsis: true, render: mono },
          {
            title: '分拣标识',
            key: 'sorting_mark',
            width: 160,
            ellipsis: true,
            render: (_: any, r: any) => {
              const code = String(r?.key || '').trim();
              const m0 = targetMarkMap.get(code);
              if (m0) return <span style={{ fontFamily: 'monospace' }}>{m0}</span>;
              // 兜底：快照未固化 target_mark_map 时，从拣货记录的 sorting_mark 聚合
              const marks = Array.from(new Set((r?.rows || []).map((x: any) => String(x?.sorting_mark || '').trim()).filter(Boolean)));
              if (!marks.length) return <span style={{ color: '#999' }}>-</span>;
              const txt = marks.slice(0, 3).join('、');
              return <span style={{ fontFamily: 'monospace' }}>{txt}{marks.length > 3 ? `…(+${marks.length - 3})` : ''}</span>;
            },
          },
          {
            title: '是否操作全部库存',
            key: 'use_all_available',
            width: 140,
            render: (_: any, r: any) => {
              const code = String(r?.key || '').trim();
              const isAll = (targetUseAllMap.has(code) ? targetUseAllMap.get(code) : useAllMapFromInstr.get(code)) || false;
              const style: React.CSSProperties = isAll
                ? { backgroundColor: '#f6ffed', color: '#389e0d', border: '1px solid #b7eb8f', borderRadius: 4, padding: '1px 8px', fontWeight: 700, display: 'inline-block' }
                : { backgroundColor: '#fff0f6', color: '#c41d7f', border: '1px solid #ffadd2', borderRadius: 4, padding: '1px 8px', fontWeight: 700, display: 'inline-block' };
              return <span style={style}>{isAll ? '是' : '否'}</span>;
            },
          },
          {
            title: '目标数量',
            key: 'target_qty',
            width: 110,
            render: (_: any, r: any) => {
              const code = String(r?.key || '').trim();
              const q0 = targetQtyMap.get(code);
              if (q0 == null) {
                // 快照里目标数量为 null/缺失：用指令 targets_detail.expected_qty 兜底
                const fb = expectedQtyMap.get(code);
                if (Number.isFinite(fb as any)) return <span style={{ fontFamily: 'monospace' }}>{String(fb)}</span>;
                return <span style={{ color: '#999' }}>-</span>;
              }
              return Number.isFinite(q0 as any) ? <span style={{ fontFamily: 'monospace' }}>{String(q0)}</span> : <span style={{ color: '#999' }}>-</span>;
            },
          },
          { title: '已拣货数量', dataIndex: 'qty', key: 'qty', width: 110 },
          {
            title: '拣货差额',
            key: 'diff_qty',
            width: 110,
            render: (_: any, r: any) => {
              const code = String(r?.key || '').trim();
              const tgt0 = targetQtyMap.get(code);
              const tgt = (tgt0 == null ? expectedQtyMap.get(code) : tgt0);
              const done = Math.floor(Number(r?.qty || 0) || 0);
              if (tgt == null || !Number.isFinite(tgt as any)) return <span style={{ color: '#999' }}>-</span>;
              const diff = Number(tgt) - done; // 差额 = 目标 - 已拣（可为负），与执行端一致
              const color = diff > 0 ? '#cf1322' : (diff < 0 ? '#fa8c16' : '#389e0d');
              return <span style={{ fontFamily: 'monospace', fontWeight: 800, color }}>{String(diff)}</span>;
            },
          },
        ]}
        scroll={{ y: 240 }}
      />

      <Divider style={{ margin: '10px 0' }} />
      <div style={{ fontWeight: 800, marginBottom: 6 }}>拣货操作记录</div>
      <Table
        size="small"
        tableLayout="fixed"
        rowKey={(_, idx) => String(idx)}
        pagination={pager(20, ['20', '50', '100', '200'])}
        dataSource={logs}
        columns={[
          { title: '容器', dataIndex: 'container_code', key: 'container_code', width: 160, ellipsis: true, render: mono },
          { title: '目标(库存单元)', dataIndex: 'target_code', key: 'target_code', width: 180, ellipsis: true, render: mono },
          { title: '分拣标识', dataIndex: 'sorting_mark', key: 'sorting_mark', width: 140, ellipsis: true, render: mono },
          { title: '数量', dataIndex: 'qty', key: 'qty', width: 90 },
          { title: '下架', dataIndex: 'off_shelf', key: 'off_shelf', width: 90, render: (v: any) => (v ? '是' : '否') },
          { title: '操作时间', dataIndex: 'operated_at', key: 'operated_at', width: 180, ellipsis: true, render: (v: any) => mono(fmtDateTime(v)) },
        ]}
        scroll={{ y: 420 }}
      />
    </div>
  );
};

export default NonStdPickSnapshotView;
